import { currentRoute, setCurrentRoute } from "./state";
import type { EventProperties } from "./types";

/** Route params as expo-router reports them (path and query params together). */
export type RouteParams = Readonly<Record<string, string | readonly string[] | undefined>>;

/** A route as expo-router describes it: its file-path segments and its params. */
export interface Route {
  readonly segments: readonly string[];
  readonly params: RouteParams;
}

/** The props of a `Screen Viewed` event. */
export interface ScreenViewedProps {
  readonly screen: string;
  readonly previous_screen?: string;
}

/**
 * Makes `route` the current route, whose `screen`, `tenant_id`, `site_id` and
 * `camera_key` go on every later event. Returns the `Screen Viewed` to send
 * when the template or a scope param (`tenantId`, `siteId`, `cameraKey`)
 * changed, and `undefined` for the same route again. Other params (filters,
 * `watching`, `from`/`to`…) neither change the route nor reach the context: the
 * component that sets such a param tracks it itself.
 */
export function observeRoute(route: Route): ScreenViewedProps | undefined {
  const screen = toTemplate(route.segments);
  const scope = scopeOf(route.params);
  const key = JSON.stringify([screen, scope]);
  const previous = currentRoute();
  if (previous?.key === key) {
    return undefined;
  }
  setCurrentRoute({ screen, key, context: { screen, ...scope } });
  return { screen, ...(previous !== undefined && { previous_screen: previous.screen }) };
}

/**
 * Route param → event property. Tenant is always in the path and site is a path
 * or query param (docs/app.practices.md § Scoping), so the route is the one
 * source for scope.
 */
const SCOPE_PARAMS = [
  ["tenantId", "tenant_id"],
  ["siteId", "site_id"],
  ["cameraKey", "camera_key"],
] as const;

/** Built in `SCOPE_PARAMS` order, so equal scopes serialise equally. */
function scopeOf(params: RouteParams): EventProperties {
  const scope: Record<string, string> = {};
  for (const [param, property] of SCOPE_PARAMS) {
    const value = firstValue(params[param]);
    if (value !== undefined) {
      scope[property] = value;
    }
  }
  return scope;
}

/** A param repeated in the query string arrives as an array; its first value is the one that counts. */
function firstValue(value: string | readonly string[] | undefined): string | undefined {
  return typeof value === "string" ? value : value?.[0];
}

/** `["(tabs)", "tenants", "[tenantId]"]` → `"/tenants/[tenantId]"`. Route groups do not appear in URLs, so they are dropped. */
function toTemplate(segments: readonly string[]): string {
  return `/${segments.filter((segment) => !isRouteGroup(segment)).join("/")}`;
}

function isRouteGroup(segment: string): boolean {
  return segment.startsWith("(") && segment.endsWith(")");
}
